// Punto de equilibrio — fórmula única para Dashboard, Rentabilidad y Reportes.
//
//   Margen de contribución = Ingresos − Costos variables
//   % Margen de contribución = Margen de contribución / Ingresos
//   Punto de equilibrio = Gastos fijos / % Margen de contribución
//
// Costos variables = todo lo que crece con la venta: CMV, gastos con categoría
// "Variable", comisiones de medios de pago, gastos de servicios. Los gastos
// "Fijo" no se restan del margen: son lo que el margen tiene que cubrir.
//
// Antes cada pantalla usaba una fórmula distinta (gastos totales / margen
// bruto, fijos / margen bruto) y los números nunca coincidían.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Lógico de empresa `punto_equilibrio_incluye_variables` (Empresa → Datos).
 * Default true. Apagado: los gastos "Variable" no restan del margen (empresas
 * que cargan compras de mercadería como gasto: ya están en el CMV).
 */
export const incluyeGastosVariables = (organization) =>
  organization?.punto_equilibrio_incluye_variables !== false;

/** Un gasto es fijo si su categoría dice "fijo" (Gastos.jsx guarda "Fijo" / "Variable"). */
export const esGastoFijo = (categoria) => String(categoria || '').toLowerCase().includes('fijo');

/** Separa una lista de gastos `{ monto, categoria }` en fijos y variables. */
export const separarGastos = (gastos = []) =>
  (Array.isArray(gastos) ? gastos : []).reduce(
    (acc, g) => {
      const m = num(g?.monto);
      if (esGastoFijo(g?.categoria)) acc.fijos += m;
      else acc.variables += m;
      return acc;
    },
    { fijos: 0, variables: 0 },
  );

/**
 * @param {{ ingresos: number, costosVariables: number, gastosFijos: number }} p
 * @returns {{
 *   puntoEquilibrio: number|null,   // null cuando no se puede calcular
 *   margenContribucion: number,
 *   margenContribucionPct: number,  // 0-100
 *   estado: 'ok'|'sin_fijos'|'sin_ventas'|'no_alcanzable',
 *   cumplida: boolean,
 *   faltante: number,
 * }}
 *
 * Estados:
 *  - sin_fijos: no hay gastos fijos → equilibrio en $0, cumplida si hubo ventas.
 *  - sin_ventas: hay fijos pero no ventas → no hay margen para calcular.
 *  - no_alcanzable: los costos variables ≥ ingresos; vender más agranda la
 *    pérdida, no existe punto de equilibrio con este margen.
 */
export const calcularPuntoEquilibrio = ({ ingresos, costosVariables, gastosFijos } = {}) => {
  const ing = num(ingresos);
  const cv = num(costosVariables);
  const cf = Math.max(0, num(gastosFijos));

  const margenContribucion = ing - cv;
  const ratio = ing > 0 ? margenContribucion / ing : 0;
  const margenContribucionPct = ratio * 100;

  // Orden: primero "no alcanzable" (con margen ≤ 0 cada venta agranda la
  // pérdida, haya o no fijos), después sin fijos, después sin ventas.
  if (ing > 0 && ratio <= 0) {
    return { puntoEquilibrio: null, margenContribucion, margenContribucionPct, estado: 'no_alcanzable', cumplida: false, faltante: 0 };
  }
  if (cf === 0) {
    return { puntoEquilibrio: 0, margenContribucion, margenContribucionPct, estado: 'sin_fijos', cumplida: ing > 0, faltante: 0 };
  }
  if (ing <= 0) {
    return { puntoEquilibrio: null, margenContribucion, margenContribucionPct, estado: 'sin_ventas', cumplida: false, faltante: 0 };
  }

  const puntoEquilibrio = cf / ratio;
  const faltante = Math.max(0, puntoEquilibrio - ing);
  // Tolerancia de centavos: ingresos == PE por redondeo cuenta como cumplida.
  const cumplida = faltante < 0.01;
  return { puntoEquilibrio, margenContribucion, margenContribucionPct, estado: 'ok', cumplida, faltante: cumplida ? 0 : faltante };
};

/** Texto corto para cuando no hay número que mostrar. */
export const leyendaPuntoEquilibrio = (estado) => {
  switch (estado) {
    case 'sin_ventas': return 'Sin ventas para calcular el margen';
    case 'no_alcanzable': return 'No alcanzable: CMV + gastos variables superan a las ventas';
    case 'sin_fijos': return 'Sin gastos fijos: revisá la categoría de tus gastos';
    default: return '';
  }
};

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** '2026-10-01' → 'oct 26'. Se parsea el string: un `date` no pasa por Date (corre un día). */
export const etiquetaMes = (mesYMD) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(mesYMD || ''));
  if (!m) return '';
  return `${MESES_CORTOS[Number(m[2]) - 1] || m[2]} ${m[1].slice(2)}`;
};

/**
 * Serie mensual para el gráfico de evolución (Reportes Inteligentes).
 * `meses` viene de la RPC get_break_even_monthly: { mes, ventas, cmv,
 * gastosFijos, gastosVariables, enCurso }. Aplica la misma fórmula y el mismo
 * lógico de empresa que el resto de las pantallas.
 * puntoEquilibrio es null cuando no hay número que graficar (sin ventas, no
 * alcanzable, o sin gastos fijos cargados: un 0 ahí se leería como "meta
 * cumplida" y en realidad faltan datos).
 */
export const armarSerieEquilibrio = (meses = [], { incluirVariables = true } = {}) =>
  (Array.isArray(meses) ? meses : []).map((m) => {
    const ventas = num(m?.ventas);
    const cmv = num(m?.cmv);
    const gastosFijos = num(m?.gastosFijos);
    const gastosVariables = num(m?.gastosVariables);
    const r = calcularPuntoEquilibrio({
      ingresos: ventas,
      costosVariables: cmv + (incluirVariables ? gastosVariables : 0),
      gastosFijos,
    });
    return {
      mes: m?.mes,
      label: etiquetaMes(m?.mes),
      enCurso: Boolean(m?.enCurso),
      ventas,
      cmv,
      gastosFijos,
      gastosVariables,
      estado: r.estado,
      cumplida: r.cumplida,
      faltante: r.faltante,
      margenContribucionPct: r.margenContribucionPct,
      puntoEquilibrio: r.estado === 'ok' ? r.puntoEquilibrio : null,
    };
  });
